// frontend/src/theme/create-theme.js
import { createTheme as createMuiTheme } from '@mui/material/styles';
import { applyPreset } from './theme-color-presets';
import { plumTheme } from './plugins/plum-theme';
import { goldTheme } from './plugins/gold-theme';

// Create theme with settings including accessibility options
export const createTheme = (language = 'en', settings = {}) => {
  const {
    direction = 'ltr',
    themeMode = 'light',
    themeColor = 'plum', // Default to plum
    typography = 'Nunito, sans-serif',
    textSize = 'small',
    contrastMode = false,
    reducedMotion = false,
  } = settings;

  // Base font size based on text size setting
  const baseFontSize = {
    small: 14,
    medium: 16,
    large: 18,
    xlarge: 20,
  }[textSize] || 14;

  // Select brand theme based on color
  let brandTheme = {};
  if (themeColor === 'plum') {
    brandTheme = plumTheme;
  } else if (themeColor === 'gold') {
    brandTheme = goldTheme;
  }

  // Create base theme
  let theme = createMuiTheme({
    direction,
    palette: {
      mode: themeMode,
      ...(brandTheme.palette || {}),
      ...(themeMode === 'dark' && {
        background: {
          default: contrastMode ? '#141a21' : '#121212',
          paper: contrastMode ? '#1c252e' : '#1e1e1e',
        },
        text: {
          primary: contrastMode ? '#FFFFFF' : '#c7cdd1',
          secondary: contrastMode ? '#c7cdd1' : '#a0a0a0',
        },
      }),
    },
    typography: {
      fontFamily: typography,
      fontSize: baseFontSize,
      h1: { fontSize: baseFontSize * 2.5, fontWeight: 700 },
      h2: { fontSize: baseFontSize * 2, fontWeight: 700 },
      h3: { fontSize: baseFontSize * 1.75, fontWeight: 600 },
      h4: { fontSize: baseFontSize * 1.5, fontWeight: 600 },
      h5: { fontSize: baseFontSize * 1.25, fontWeight: 500 },
      h6: { fontSize: baseFontSize * 1.1, fontWeight: 500 },
      body1: { fontSize: baseFontSize },
      body2: { fontSize: baseFontSize * 0.875 },
      button: { fontWeight: 600, textTransform: 'none' },
    },
    shape: {
      borderRadius: 8,
    },
    customShadows: brandTheme.customShadows || {},
    gradients: brandTheme.gradients || {},
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            // Increase padding for better touch targets
            padding: textSize === 'large' || textSize === 'xlarge' 
              ? '8px 20px' 
              : undefined,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          contained: {
            boxShadow: brandTheme.customShadows?.button || 'none',
            '&:hover': {
              boxShadow: brandTheme.customShadows?.button || 'none',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: brandTheme.customShadows?.card || 'none',
            borderRadius: 12,
            position: 'relative',
            zIndex: 0,
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 8,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            boxShadow: brandTheme.customShadows?.dialog || 'none',
            borderRadius: 12,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: baseFontSize * 0.75,
            backgroundColor: themeMode === 'light' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)',
            color: themeMode === 'light' ? '#fff' : '#000',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: baseFontSize,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            // Enhance link underlines for better visibility
            textDecorationThickness: contrastMode ? '2px' : undefined,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            // Apply reduced motion if enabled
            ...(reducedMotion && {
              '& *': {
                animationDuration: '0.01ms !important',
                animationIterationCount: '1 !important',
                transitionDuration: '0.01ms !important',
                scrollBehavior: 'auto !important',
              },
            }),
          },
        },
      },
    },
  });

  // Apply color preset if selected but not already applied via brandTheme
  if (themeColor !== 'plum' && themeColor !== 'gold') {
    theme = applyPreset(theme, themeColor);
  }

  return theme;
};

export default createTheme;